import type { Course, TutorialStep } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";

import Tutorial from "~/components/Tutorial";
import { useAccess } from "~/hooks";

const CourseTutorial = ({ course }: { course: Course }) => {
	const { isLoaded } = useAuth();

	const hasAudio = !!course.audio;

	const { isMember } = useAccess();

	const steps: TutorialStep[] = [
		{
			content: "Welcome to your first course. Let's get you started!",
			target: "#course-title",
		},

		{
			content: "The number of credits you will earn for this course.",
			target: "#course-credits",
			disableScrolling: true,
		},

		{
			content: "Click on a chapter to expand and start reading!",
			target: ".course-section",
		},

		{
			content:
				"This icon marks sections that are critical study material for passing the final exam.",
			target: "#test-critical-content-icon",
		},

		hasAudio && {
			content: "Listen to the audio for this chapter.",
			target: ".course-section-audio-btn",
		},

		{
			content:
				"Mark this chapter as complete when you are done to track your progress and pick up where you left off.",
			target: ".course-section-progress-btn",
		},

		{
			content: "Open and close boxes to make navigation easier.",
			target: ".course-section-expand-btn",
		},

		{
			content:
				"Go through each chapter to complete the course and prepare for the final exam.",
			target: "#course-content",
		},

		{
			content: "Expand and collapse all chapters.",
			target: "#course-expand-all-btn",
		},

		{
			content:
				"Take a practice exam to see if you are ready to take the final exam. You can take the practice exam as many times as you want.",
			target: "#practice-exam-btn",
		},
		{
			content:
				"Take a practice exam to see if you are ready to take the final exam. You can take the practice exam as many times as you want.",
			target: "#practice-exam-btn-mobile",
		},

		{
			content:
				"Take the final exam to pass the course and earn your credits. You can take the final exam 2 times. After failing twice, you will need to wait one month before retrying.",
			target: "#final-exam-btn",
		},

		{
			content:
				"Take the final exam to pass the course and earn your credits. You can take the final exam 2 times. After failing twice, you will need to wait one month before retrying.",
			target: "#final-exam-btn-mobile",
		},

		{
			content: "Bookmark this course and save it your dashboard!",
			target: "#course-bookmark-btn",
			disableScrolling: true,
		},

		hasAudio && {
			content:
				"Listen to the audio read through the entire course by subject matter experts.",
			target: "#course-audio-player",
		},

		{
			content: "Related courses are courses in the same sub-category.",
			target: "#course-recommended-courses",
		},

		{
			content:
				"All courses are accredited, click here to see accrediting organizations, target audience, and course objectives.",
			target: "#course-info-btn",
			disableScrolling: true,
		},

		{
			content:
				"Ask questions, share your thoughts, and engage with other students.",
			target: "#course-comments",
		},

		{
			content: "Share this course with your friends and colleagues.",
			target: "#course-share-btn",
		},

		isMember
			? null
			: {
					content:
						"Become a member today to get course access & start earning credits!",
					target: ".sidebar-cta",
				},
		isMember
			? null
			: {
					content:
						"Become a member today to get course access & start earning credits!",
					target: ".sidebar-cta-mobile",
				},
	].filter(Boolean) as TutorialStep[];

	if (!isLoaded) return null;

	return (
		<Tutorial
			steps={steps}
			tutorialKey="courseTutorial"
			trackingEventTitle="Course Tutorial"
		/>
	);
};

export default CourseTutorial;
